/**
 * This is used to validate environment variables.
 * You can use the `env` object to access the environment variables.
 * If you use a server environment variable in the client, you will get an error.
 */

import { createEnv } from "@t3-oss/env-nextjs"
import { env as runTimeEnv } from "next-runtime-env"
import { z } from "zod"

// Allows for easy renaming as you see fit across the entire project
export const EnvKeys = {
  BUNDLE_ANALYSIS_ENABLED: "BUNDLE_ANALYSIS_ENABLED",
  CI: "CI",
  NEXT_OUTPUT: "NEXT_OUTPUT",
  NEXT_PUBLIC_BACKEND_PORT: "NEXT_PUBLIC_BACKEND_PORT",
  NEXT_PUBLIC_ENV: "NEXT_PUBLIC_ENV",
  NEXT_RUNTIME: "NEXT_RUNTIME",
  NODE_ENV: "NODE_ENV",
  NEXT_PUBLIC_MOCK_DATA: "NEXT_PUBLIC_MOCK_DATA",
  NEXT_PUBLIC_API_URL: "NEXT_PUBLIC_API_URL",
  NEXT_PUBLIC_SCOREBOARD_URL: "NEXT_PUBLIC_SCOREBOARD_URL",
  API_URL: "API_URL",
  APP_VERSION: "APP_VERSION",
  COMMIT_HASH: "COMMIT_HASH",
  NEXT_PUBLIC_S3_BUCKET_NAME: "NEXT_PUBLIC_S3_BUCKET_NAME",
  NEXT_PUBLIC_LOCALES: "NEXT_PUBLIC_LOCALES",
  NEXT_PUBLIC_DEFAULT_LOCALE: "NEXT_PUBLIC_DEFAULT_LOCALE",
  NEXT_PUBLIC_CHAMPIONSHIP: "NEXT_PUBLIC_CHAMPIONSHIP",
} as const

// Define server variables
const server = {
  /** Enable bundle analysis */
  [EnvKeys.BUNDLE_ANALYSIS_ENABLED]: z
    .enum(["true", "false"])
    .default("false")
    .transform(s => s === "true"),
  /** CI Mode */
  [EnvKeys.CI]: z
    .enum(["true", "false"])
    .default("false")
    .transform(s => s === "true"),
  /**
   * The Next.js output target.
   * export: Static HTML export.
   * standalone: Docker
   * '' (empty string): Default behavior.
   */
  [EnvKeys.NEXT_OUTPUT]: z.enum(["export", "standalone"]).optional(),
  /**
   * The runtime the server will run on.
   * This environment variable is set by Next.js and does not need to be set via the .env file.
   */
  [EnvKeys.NEXT_RUNTIME]: z.enum(["nodejs", "edge"]).optional(),
  /**
   * The API URL.
   * This is the URL of the API that the server will use to communicate with the client.
   */
  [EnvKeys.API_URL]: z.string().url().optional(),
}

// Define client variables
const client = {
  /** The port the backend will run on */
  [EnvKeys.NEXT_PUBLIC_BACKEND_PORT]: z
    .string()
    .default("5000")
    .transform(s => Number.parseInt(s, 10))
    .pipe(z.number()),
  /** The app environment */
  [EnvKeys.NEXT_PUBLIC_ENV]: z.string().default("development"),
  /** Enable mock data */
  [EnvKeys.NEXT_PUBLIC_MOCK_DATA]: z
    .enum(["true", "false"])
    .default("false")
    .transform(s => s === "true"),
  /** The API URL */
  [EnvKeys.NEXT_PUBLIC_API_URL]: z.string().url().optional(),
  /** The Scoreboard URL */
  [EnvKeys.NEXT_PUBLIC_SCOREBOARD_URL]: z.string().url().optional(),
  /** The S3 bucket name */
  [EnvKeys.NEXT_PUBLIC_S3_BUCKET_NAME]: z.string().optional(),
  /** Supported locales for internationalization */
  [EnvKeys.NEXT_PUBLIC_LOCALES]: z
    .string()
    .default("en")
    .transform(s => s.split(",").map(locale => locale.trim())),
  /** Default locale for internationalization */
  [EnvKeys.NEXT_PUBLIC_DEFAULT_LOCALE]: z.string().default("en"),
  [EnvKeys.NEXT_PUBLIC_CHAMPIONSHIP]: z.string().default("CIS"),
}

// Define shared variables
const shared = {
  /** Node environment can run in development or production */
  [EnvKeys.NODE_ENV]: z.enum(["development", "production"]).default(process.env.NODE_ENV as "development" | "production" || "development"),
  /** The app version */
  [EnvKeys.APP_VERSION]: z.string().default(process.env.APP_VERSION || "0.0.0"),
  /** The app commit hash */
  [EnvKeys.COMMIT_HASH]: z.string().default(process.env.COMMIT_HASH || ""),

}

// Shared envs should be mapped via process.env
const sharedEnv = {
  ...Object.fromEntries(
    Object.keys(shared).map(key => [key, process.env[key as keyof typeof shared]]),
  ) as Record<keyof typeof shared, string | undefined>,
}

// Public envs should be mapped via `next-runtime-env`
const publicEnv = Object.fromEntries(
  Object.keys(client).map(key => [key, runTimeEnv(key as keyof typeof client)]),
) as Record<keyof typeof client, string | undefined>

// Combine
const experimental__runtimeEnv = {
  ...sharedEnv,
  ...publicEnv,
}

// Create the env
export const env = createEnv({
  client,
  emptyStringAsUndefined: true,
  experimental__runtimeEnv,
  server,
  shared,
  skipValidation: process.env.NODE_ENV === "test",
})
